import * as React from "react";
import axios from "axios";
import { observer } from "mobx-react";
import call from "../../call";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Alert from "@mui/material/Alert";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Title from "./Title";
import { UserDataStoreContext } from "../../store";
import {
  MAX_CS_OWNER_EMAILS,
  areValidEmails,
  duplicateIndexes,
  isValidEmail,
  normalizeEmails,
} from "./csOwnerEmails";

// Kept in step with ReminderSchedule::DEFAULT_* on the backend.
const DEFAULT_CADENCE_DAYS = 14;

// An empty day field means "use the default", so it counts as valid.
const isValidDays = (value) => {
  if (value.trim() === "") {
    return true;
  }
  const days = Number(value);
  return Number.isInteger(days) && days >= 1 && days <= 365;
};

const daysOrDefault = (value, fallback) => (value.trim() === "" ? fallback : Number(value));

// The editable fields, normalised so the loaded client and the current form are compared through
// one shape — an empty day field and its default, or a blank email row, must not read as a change.
const snapshot = ({
  name,
  isDisabled,
  csOwnerEmails,
  isBackupCsRequest,
  cadenceDays,
  monitoringEnabled,
}) => ({
  name,
  isDisabled,
  csOwnerEmails: normalizeEmails(csOwnerEmails),
  isBackupCsRequest,
  cadenceDays: daysOrDefault(cadenceDays, DEFAULT_CADENCE_DAYS),
  monitoringEnabled,
});

const formatTimestamp = (value) => (value ? new Date(value).toLocaleString() : "Not yet activated");

const csOwnerEmailHelperText = (index, isMalformed, isDuplicate) => {
  if (isMalformed) {
    return "Not a valid email address.";
  }
  if (isDuplicate) {
    return "This address is already in the list.";
  }
  if (index === 0) {
    return "Where delivery reminders are sent. Take it from the token request email.";
  }
  return "";
};

const ClientForm = observer(() => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // Without a backup CS the signed-in user is the CS owner, and the server stamps their address
  // on save — this is only to show them where the reminders will land.
  const userDataStore = React.useContext(UserDataStoreContext);
  const currentUserEmail = userDataStore.user?.email || "";

  const [loading, setLoading] = React.useState(false);
  const [rotating, setRotating] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = React.useState(false);

  const [name, setName] = React.useState("");
  const [token, setToken] = React.useState("");
  const [isDisabled, setIsDisabled] = React.useState(false);

  // One string per input; always at least one, so the first row is there to type into.
  const [csOwnerEmails, setCsOwnerEmails] = React.useState([""]);
  const [isBackupCsRequest, setIsBackupCsRequest] = React.useState(false);
  // Held as strings so the inputs can legitimately be empty while being typed.
  const [cadenceDays, setCadenceDays] = React.useState(String(DEFAULT_CADENCE_DAYS));
  const [monitoringEnabled, setMonitoringEnabled] = React.useState(true);
  const [monitoringBaselineAt, setMonitoringBaselineAt] = React.useState(null);
  const [lastListReceivedAt, setLastListReceivedAt] = React.useState(null);
  // Snapshot of the last server response, so Save can tell whether anything actually changed.
  const [baseline, setBaseline] = React.useState(null);

  React.useEffect(() => {
    if (!isEdit) {
      return;
    }

    setLoading(true);
    setError("");
    call(
      axios({
        method: "get",
        url: `/api/demandbase/client/${id}`,
        headers: {
          Accept: "application/json",
        },
      }),
    )
      .then((resp) => {
        applyClient(resp.data);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        setError(
          e.response?.data?.error
            ? e.response.data.error
            : "An error occured on trying to load the client!",
        );
      });
  }, [id]);

  // Applies a client from the server and re-anchors the dirty baseline to it, so a freshly
  // loaded or freshly saved form reads as pristine.
  const applyClient = (c) => {
    const loaded = {
      name: c.name || "",
      isDisabled: Boolean(c.isDisabled),
      csOwnerEmails: c.csOwnerEmails?.length ? c.csOwnerEmails : [""],
      isBackupCsRequest: Boolean(c.isBackupCsRequest),
      cadenceDays: String(c.expectedCadenceDays ?? DEFAULT_CADENCE_DAYS),
      monitoringEnabled: c.monitoringEnabled !== false,
    };

    setName(loaded.name);
    setToken(c.apiToken || "");
    setIsDisabled(loaded.isDisabled);
    setCsOwnerEmails(loaded.csOwnerEmails);
    setIsBackupCsRequest(loaded.isBackupCsRequest);
    setCadenceDays(loaded.cadenceDays);
    setMonitoringEnabled(loaded.monitoringEnabled);
    setMonitoringBaselineAt(c.monitoringBaselineAt || null);
    setLastListReceivedAt(c.lastListReceivedAt || null);
    setBaseline(snapshot(loaded));
  };

  // The typed addresses are always sent, but the server only honours them for a backup-CS
  // request: with the box unticked it re-stamps the client with the signed-in user's address.
  const monitoringPayload = () => ({
    cs_owner_emails: normalizeEmails(csOwnerEmails),
    expected_cadence_days: daysOrDefault(cadenceDays, DEFAULT_CADENCE_DAYS),
    monitoring_enabled: monitoringEnabled,
    is_backup_cs_request: isBackupCsRequest,
  });

  const handleCreate = () => {
    setLoading(true);
    setError("");

    const payload = {
      name: name,
      ...monitoringPayload(),
    };

    call(
      axios({
        method: "post",
        url: "/api/demandbase/client",
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain",
        },
      }),
    )
      .then((resp) => {
        setLoading(false);
        const newId = resp.data?.id;
        if (newId) {
          navigate(`/integrations/demandbase/clients/${newId}/edit`, { replace: true });
        } else {
          navigate("/integrations/demandbase?view=clients", { replace: true });
        }
      })
      .catch((e) => {
        setLoading(false);
        setError(
          e.response?.data?.error
            ? e.response.data.error
            : "An error occured on trying to create the client!",
        );
      });
  };

  const handleSaveDetails = () => {
    setLoading(true);
    setError("");
    setSuccess("");
    setSuccessOpen(false);

    const payload = {
      id: Number(id),
      name: name,
      is_disabled: isDisabled,
      ...monitoringPayload(),
    };

    call(
      axios({
        method: "put",
        url: "/api/demandbase/client",
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain",
        },
      }),
    )
      .then((resp) => {
        setLoading(false);
        applyClient(resp.data);

        setSuccess("Changes saved");
        setSuccessOpen(true);
      })
      .catch((e) => {
        setLoading(false);
        setError(
          e.response?.data?.error
            ? e.response.data.error
            : "An error occured on trying to save changes!",
        );
      });
  };

  const handleRotateToken = () => {
    setRotating(true);
    setError("");
    setSuccess("");
    setSuccessOpen(false);

    call(
      axios({
        method: "post",
        url: `/api/demandbase/client/${id}/rotate-token`,
        headers: {
          Accept: "application/json",
        },
      }),
    )
      .then((resp) => {
        const c = resp.data;
        setToken(c.apiToken || "");
        setRotating(false);

        setSuccess("Token rotated");
        setSuccessOpen(true);
      })
      .catch((e) => {
        setRotating(false);
        setError(
          e.response?.data?.error
            ? e.response.data.error
            : "An error occured on trying to rotate token!",
        );
      });
  };

  const updateCsOwnerEmail = (index, value) =>
    setCsOwnerEmails((rows) => rows.map((row, i) => (i === index ? value : row)));
  const removeCsOwnerEmail = (index) =>
    setCsOwnerEmails((rows) => rows.filter((_, i) => i !== index));

  // The addresses only have to be given for a backup-CS request; otherwise the server fills them
  // in from the session, so blank inputs must never block saving.
  const duplicateEmailRows = duplicateIndexes(csOwnerEmails);
  const monitoringValid =
    isValidDays(cadenceDays) &&
    (!isBackupCsRequest || (areValidEmails(csOwnerEmails) && duplicateEmailRows.size === 0));
  const isDirty =
    baseline !== null &&
    JSON.stringify(
      snapshot({
        name,
        isDisabled,
        csOwnerEmails,
        isBackupCsRequest,
        cadenceDays,
        monitoringEnabled,
      }),
    ) !== JSON.stringify(baseline);
  const canCreate = name.trim().length > 0 && monitoringValid;
  const canSaveDetails = name.trim().length > 0 && monitoringValid && isDirty;

  const savedOwners = normalizeEmails(csOwnerEmails);
  const currentOwnersNote =
    savedOwners.length > 0 && !(savedOwners.length === 1 && savedOwners[0] === currentUserEmail)
      ? ` Currently set to ${savedOwners.join(", ")}; saving will move them to you.`
      : "";

  const monitoringFields = (
    <React.Fragment>
      <FormControlLabel
        control={
          <Checkbox
            checked={isBackupCsRequest}
            onChange={(e) => setIsBackupCsRequest(e.target.checked)}
            disabled={loading || rotating}
          />
        }
        label="Backup CS acting on behalf of main CS?"
      />

      {isBackupCsRequest && (
        <React.Fragment>
          <Alert severity="info">
            Enter the <strong>main</strong> CS owner's address below — reminders must not go to the
            backup.
          </Alert>

          {csOwnerEmails.map((email, index) => {
            const trimmed = email.trim();
            const isDuplicate = duplicateEmailRows.has(index);
            const isMalformed = trimmed !== "" && !isValidEmail(trimmed);

            return (
              <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  margin="normal"
                  fullWidth
                  type="email"
                  label={index === 0 ? "CS owner email" : `CS owner email ${index + 1}`}
                  name={`cs_owner_emails[${index}]`}
                  value={email}
                  onChange={(e) => updateCsOwnerEmail(index, e.target.value)}
                  error={isMalformed || isDuplicate}
                  helperText={csOwnerEmailHelperText(index, isMalformed, isDuplicate)}
                  disabled={loading || rotating}
                />
                {csOwnerEmails.length > 1 && (
                  <IconButton
                    aria-label={`Remove CS owner email ${index + 1}`}
                    onClick={() => removeCsOwnerEmail(index)}
                    disabled={loading || rotating}
                    sx={{ mt: 3 }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Stack>
            );
          })}

          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setCsOwnerEmails((rows) => [...rows, ""])}
              disabled={
                loading ||
                rotating ||
                csOwnerEmails.length >= MAX_CS_OWNER_EMAILS ||
                csOwnerEmails[csOwnerEmails.length - 1].trim() === ""
              }
            >
              Add email
            </Button>
            {csOwnerEmails.length >= MAX_CS_OWNER_EMAILS && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Up to {MAX_CS_OWNER_EMAILS} addresses.
              </Typography>
            )}
          </Box>
        </React.Fragment>
      )}

      {!isBackupCsRequest && (
        <Alert severity="info">
          Delivery reminders go to you — <strong>{currentUserEmail}</strong>.
          {currentOwnersNote}
        </Alert>
      )}

      <FormControl sx={{ width: "100%" }}>
        <TextField
          margin="normal"
          fullWidth
          type="number"
          label="Expected cadence (days)"
          name="expected_cadence_days"
          value={cadenceDays}
          onChange={(e) => setCadenceDays(e.target.value)}
          inputProps={{ min: 1, max: 365, step: 1 }}
          error={!isValidDays(cadenceDays)}
          helperText={`How often lists are expected. Leave empty for the default of ${DEFAULT_CADENCE_DAYS} days.`}
          disabled={loading || rotating}
        />
      </FormControl>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Title>{isEdit ? "Edit Client" : "Add Client"}</Title>
      </Box>

      <Stack sx={{ width: "100%" }} spacing={2}>
        {error && error.length > 0 ? <Alert severity="error">{error}</Alert> : ""}

        {!isEdit && (
          <React.Fragment>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </FormControl>

            {monitoringFields}

            <ButtonGroup variant="contained">
              <LoadingButton
                fullWidth
                loading={loading}
                onClick={handleCreate}
                disabled={!canCreate || loading}
              >
                Generate token
              </LoadingButton>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/integrations/demandbase?view=clients")}
                disabled={loading}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </React.Fragment>
        )}

        {isEdit && (
          <React.Fragment>
            <Paper sx={{ p: 2 }}>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading || rotating}
                />
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={!isDisabled}
                    onChange={(e) => setIsDisabled(!e.target.checked)}
                    disabled={loading || rotating}
                  />
                }
                label={isDisabled ? "Disabled" : "Active"}
              />

              <Typography variant="subtitle1" sx={{ mt: 3 }}>
                Delivery monitoring
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={monitoringEnabled}
                    onChange={(e) => setMonitoringEnabled(e.target.checked)}
                    disabled={loading || rotating}
                  />
                }
                label={monitoringEnabled ? "Reminders on" : "Reminders off"}
              />

              {monitoringFields}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Monitoring active since: {formatTimestamp(monitoringBaselineAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Last list received: {formatTimestamp(lastListReceivedAt)}
              </Typography>

              <ButtonGroup variant="contained" sx={{ width: "100%" }}>
                <LoadingButton
                  fullWidth
                  loading={loading}
                  onClick={handleSaveDetails}
                  disabled={!canSaveDetails || loading || rotating}
                >
                  Save changes
                </LoadingButton>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate("/integrations/demandbase?view=clients")}
                  disabled={loading || rotating}
                >
                  Back
                </Button>
              </ButtonGroup>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Token"
                  name="api_token"
                  value={token}
                  helperText="Rotating token will invalidate the previous token"
                  disabled={true}
                  inputProps={{ readOnly: true }}
                />
              </FormControl>

              <LoadingButton
                variant="outlined"
                loading={rotating}
                onClick={() => setShowRotateConfirm(true)}
                disabled={loading || rotating}
              >
                Rotate token
              </LoadingButton>
            </Paper>
          </React.Fragment>
        )}

        <Dialog
          open={showRotateConfirm}
          onClose={() => setShowRotateConfirm(false)}
        >
          <DialogTitle>Rotate token</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to rotate the token? The previous token will stop working.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowRotateConfirm(false)}
              disabled={rotating}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              loading={rotating}
              onClick={() => {
                setShowRotateConfirm(false);
                handleRotateToken();
              }}
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccessOpen(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            {success}
          </Alert>
        </Snackbar>
      </Stack>
    </React.Fragment>
  );
});

export default ClientForm;
